import { IconButton } from '@atlaskit/button/new';
import CloseIcon from '@atlaskit/icon/glyph/editor/close';
import PresenceActiveIcon from '@atlaskit/icon/glyph/presence-active';
import { Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { MenuLinkItem } from '@atlassian/navigation-system/side-nav/menu-link-item';
import type { ReactNode } from 'react';
import { useRouter } from 'react-resource-router';

import { useAnalyticsWebClient } from '../../../../utils/analytics/context';
import { useConfirmationModal } from '../../hooks/use-confirmation-modal';
import { useCompleteFlow } from '../hooks/use-complete-flow';

const getColor = (status: string) => {
	if (status === 'IN_PROGRESS') {
		return token('color.icon.accent.lime');
	}

	return token('color.icon.accent.yellow');
};

const StatusIcon = ({ status }: { status: string }) => {
	const color = getColor(status);

	return (
		<Tooltip content={`Status: ${status}`}>
			<PresenceActiveIcon primaryColor={color} label={status} />
		</Tooltip>
	);
};

const TerminateFlowButton = ({
	onClick,
	isDisabled,
}: {
	onClick: (e: React.MouseEvent) => void;
	isDisabled: boolean;
}) => {
	return (
		<Tooltip content="Terminate this flow">
			<IconButton
				testId="terminate-flow-button"
				appearance="subtle"
				onClick={onClick}
				isDisabled={isDisabled}
				spacing="compact"
				icon={CloseIcon}
				label="Terminate this flow"
			/>
		</Tooltip>
	);
};

type NavigationItemProps = {
	icon: ReactNode;
	children: ReactNode;
	status: string;
	href: string;
	id?: string;
	flowARI?: string;
};

export const NavigationItem = ({
	icon,
	children,
	href,
	status,
	id,
	flowARI,
}: NavigationItemProps) => {
	const [routeState, routerActions] = useRouter();
	const analyticsWebClient = useAnalyticsWebClient();
	const [completeSession, isCompletePending] = useCompleteFlow();
	const { openConfirmationModal, ConfirmationModal } = useConfirmationModal({
		title: 'Are you sure you want to terminate this flow?',
		confirmBody: (
			<p>This operation is irreversible, please ensure you have saved all your work. </p>
		),
		onConfirm: () => {
			if (id && flowARI) {
				completeSession(id, flowARI);
			}
		},
	});

	const handleComplete = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (id && flowARI) {
			openConfirmationModal();
		}
	};

	if (!id || !flowARI) {
		return null;
	}

	return (
		<MenuLinkItem
			href={href}
			elemBefore={icon}
			elemAfter={
				<Inline>
					<StatusIcon status={status} />
					<TerminateFlowButton onClick={handleComplete} isDisabled={isCompletePending} />
				</Inline>
			}
			isSelected={routeState.match.url === `${routeState.basePath}${href}`}
			onClick={(e) => {
				if (href) {
					e.preventDefault();
					routerActions.push(href);

					analyticsWebClient.sendUIEvent({
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'boysenberry-navigation-item',
						source: 'boysenberry-sidebar',
					});
				}
			}}
		>
			{children}
			{ConfirmationModal}
		</MenuLinkItem>
	);
};
